import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Building2, Plus, Trash2, Pencil, Star } from "lucide-react";

const EMPTY = { name: "", address: "", phone: "", razao_social: "", cnpj: "", active: true, google_review_url: "" };

export default function Units() {
  const [units, setUnits] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => setUnits((await api.get("/units")).data);
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ name: u.name, address: u.address || "", phone: u.phone || "", razao_social: u.razao_social || "", cnpj: u.cnpj || "", active: u.active !== false, google_review_url: u.google_review_url || "" }); setOpen(true); };
  const save = async () => {
    if (!form.name) return toast.error("Nome obrigatório");
    try {
      if (editing) { await api.patch(`/units/${editing.id}`, form); toast.success("Unidade atualizada"); }
      else { await api.post("/units", form); toast.success("Unidade criada"); }
      setOpen(false); setForm(EMPTY); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remover unidade? Alunos e usuários vinculados ficarão sem unidade.")) return;
    await api.delete(`/units/${id}`); toast.success("Removida"); load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900">Unidades</h1>
          <p className="text-slate-500">Filiais da sua rede — alunos circulam entre todas</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid="unit-new-button" className="bg-rose-600 hover:bg-rose-700"><Plus className="w-4 h-4 mr-2"/>Nova unidade</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Editar unidade" : "Nova unidade"}</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-4">
              <div><Label>Nome</Label><Input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} data-testid="unit-name-input"/></div>
              <div><Label>Endereço</Label><Input value={form.address} onChange={(e)=>setForm({...form,address:e.target.value})}/></div>
              <div><Label>Telefone</Label><Input value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})}/></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Razão Social</Label>
                  <Input value={form.razao_social} onChange={(e)=>setForm({...form,razao_social:e.target.value})}
                         placeholder="Ex: American Fit Ltda" data-testid="unit-razao-social-input"/>
                  <p className="text-[10px] text-slate-500 mt-1">Aparece no cabeçalho do recibo de pagamento.</p>
                </div>
                <div>
                  <Label>CNPJ</Label>
                  <Input value={form.cnpj} onChange={(e)=>setForm({...form,cnpj:e.target.value})}
                         placeholder="00.000.000/0000-00" data-testid="unit-cnpj-input"/>
                  <p className="text-[10px] text-slate-500 mt-1">Aparece no cabeçalho do recibo.</p>
                </div>
              </div>
              <div>
                <Label className="flex items-center gap-1"><Star className="w-3 h-3 text-amber-500"/>Link de avaliação no Google</Label>
                <Input value={form.google_review_url} onChange={(e)=>setForm({...form,google_review_url:e.target.value})}
                       placeholder="https://g.page/r/CxxxxxxYY/review" data-testid="unit-google-review"/>
                <p className="text-[10px] text-slate-500 mt-1">Cole a URL "Avaliar" do Google Meu Negócio dessa unidade. Aparece no portal do aluno para incentivar avaliações 5 estrelas.</p>
              </div>
              <Button onClick={save} data-testid="unit-save-button" className="w-full bg-rose-600 hover:bg-rose-700">{editing ? "Salvar alterações" : "Criar unidade"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="units-list">
        {units.map(u => (
          <Card key={u.id} className="p-6 border border-slate-200 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 rounded-lg bg-rose-50 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-rose-600"/>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" onClick={()=>openEdit(u)} data-testid={`unit-edit-${u.id}`}><Pencil className="w-4 h-4 text-slate-400"/></Button>
                <Button variant="ghost" size="icon" onClick={()=>remove(u.id)}><Trash2 className="w-4 h-4 text-slate-400"/></Button>
              </div>
            </div>
            <h3 className="font-heading text-xl font-bold text-slate-900 mt-4">{u.name}</h3>
            <p className="text-sm text-slate-500 mt-1">{u.address || "sem endereço"}</p>
            {u.phone && <p className="text-xs text-slate-500 font-mono mt-1">{u.phone}</p>}
            {u.google_review_url ? (
              <div className="mt-3 flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-amber-600" data-testid={`unit-google-badge-${u.id}`}>
                <Star className="w-3 h-3 fill-current"/>Avaliação Google configurada
              </div>
            ) : (
              <div className="mt-3 text-[10px] uppercase tracking-widest text-slate-400 font-bold">⚠ Sem link do Google</div>
            )}
          </Card>
        ))}
        {units.length === 0 && <p className="text-slate-500 col-span-full text-center py-12">Nenhuma unidade cadastrada</p>}
      </div>
    </div>
  );
}
